//react
import React, { useEffect, useMemo } from "react";
import Highlighter from "react-highlight-words";

//UI
import { Row, Col, Table, Empty, message } from "antd";
import { HomePageTable } from "../../../Common/UIElements";
import PropertyContactUsersTopBar from "../../PropertyContactUsersTopBar";
import LoadingComponent from "../../../Common/LoadingComponent";
import {
  ColoredTextTag,
  TEXT_TAG_COLORS,
} from "../../../Common/ColoredTextTag/ColoredTextTag";

//processing
import { tableStringSorter } from "../../../../utils/table";
import {
  useActivateOnboardingUser,
  useInviteOnboardingUser,
} from "../../../../hooks/Onboarding/onboarding-users.hooks";
import { useGetAllProperties } from "../../../../hooks/Onboarding/onboarding-properties.hooks";
import useQuery from "../../../../hooks/url.hooks";
import useTableSearch from "../../../../hooks/table-search.hooks";
import {
  ONBOARDING_PROPERTY_CONTACT_STATUS,
  ONBOARDING_PROPERTY_STATUS,
  RECORD_OPERATIONS,
} from "../../../../utils/onboarding-helper";
import { ButtonLink } from "../../../Common/Button";

// set table height according to window
let tableHeight = 500;
if (window.innerWidth > 700) {
  tableHeight = window.innerHeight - 375;
}

const PropertyContactUsers = (props) => {
  const {
    addPropertyUser,
    customer,
    setCustomer,
    setOperation,
    setOperationData,
    openDrawer,
  } = props;

  let query = useQuery();
  const msg = query.get("msg");

  useEffect(() => {
    if (msg) message.success(msg);
  }, []);

  const { mutateAsync: activatePropertyContact } = useActivateOnboardingUser({
    onSuccess: () => {
      message.success("Activation request successfully sent");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const { mutateAsync: invitePropertyContact } = useInviteOnboardingUser({
    onSuccess: () => {
      message.success("Reactivation request successfully sent");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const triggerDeactivatePropertyContact = (id) => {
    console.log("Deactivating ::", id);
  };

  const triggerActivatePropertyContact = (id) => {
    console.log("Activating ::", id);
    activatePropertyContact({ id: id });
  };

  const triggerResendInvitation = (id) => {
    console.log("Resending invite ::", id);
    invitePropertyContact({ id: id });
  };

  //render component for hilightable search text
  const searchHilighter = (dataIndex, customRender) => ({
    render: (text) => {
      return searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={searchedWords}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : customRender ? (
        customRender(text)
      ) : (
        text
      );
    },
  });

  //table columns
  const columns = [
    {
      title: "Property Name",
      dataIndex: "property_name",
      key: "property_name",
      width: "200px",
      sorter: tableStringSorter("property_name"),
      fixed: "left",
      ...searchHilighter("property_name"),
    },
    {
      title: "Property Contact First Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "250px",
      sorter: tableStringSorter("first_name"),
      ...searchHilighter("first_name"),
    },
    {
      title: "Property Contact Last Name",
      dataIndex: "last_name",
      key: "last_name",
      width: "250px",
      sorter: tableStringSorter("last_name"),
      ...searchHilighter("last_name"),
    },
    {
      title: "Property Contact Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
      width: "275px",
      sorter: tableStringSorter("phone_number"),
      ...searchHilighter("phone_number"),
    },
    {
      title: "Property Contact Email",
      dataIndex: "email",
      key: "email",
      width: "230px",
      sorter: tableStringSorter("email"),
      ...searchHilighter("email"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "100px",
      fixed: "right",
      sorter: tableStringSorter("status"),
      ...searchHilighter("status", (status) => {
        let textColor =
          status === ONBOARDING_PROPERTY_CONTACT_STATUS.ACTIVE
            ? TEXT_TAG_COLORS.SUCCESS
            : status === ONBOARDING_PROPERTY_CONTACT_STATUS.PENDING
            ? TEXT_TAG_COLORS.WARN
            : "";

        return <ColoredTextTag text={status} color={textColor} />;
      }),
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "_id",
      width: "200px",
      render: (_id, record) => {
        const status = record.status;
        return (
          <Row>
            <Col>
              {status === ONBOARDING_PROPERTY_CONTACT_STATUS.ACTIVE && (
                <ButtonLink
                  onClick={() => {
                    triggerDeactivatePropertyContact(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>
                    Deactivate
                  </span>
                </ButtonLink>
              )}
              {status === ONBOARDING_PROPERTY_CONTACT_STATUS.IN_DRAFT && (
                <ButtonLink
                  onClick={() => {
                    triggerActivatePropertyContact(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>Activate</span>
                </ButtonLink>
              )}
              {status === ONBOARDING_PROPERTY_CONTACT_STATUS.PENDING && (
                <ButtonLink
                  onClick={() => {
                    triggerResendInvitation(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>
                    Resend Invitation
                  </span>
                </ButtonLink>
              )}
              {(status === ONBOARDING_PROPERTY_CONTACT_STATUS.IN_DRAFT ||
                status !== ONBOARDING_PROPERTY_CONTACT_STATUS.PENDING) && (
                <ButtonLink
                  onClick={() => {
                    setOperation(RECORD_OPERATIONS.EDIT);
                    setOperationData(record);
                    openDrawer();
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>Edit</span>
                </ButtonLink>
              )}
            </Col>
          </Row>
        );
      },
      fixed: "right",
    },
  ];

  //columns that could be searched
  const searchableColumns = columns.map((col) => col.dataIndex);

  const {
    data: apiAllPropertyContactData,
    isFetched: isFetchedAllPropertContacts,
  } = useGetAllProperties({
    select: (data) => {
      const updatedData = [...data];
      return updatedData.map((e) => {
        return {
          ...e,
          _id: e?.property_contact_id?._id,
          first_name: e?.property_contact_id?.first_name,
          last_name: e?.property_contact_id?.last_name,
          phone_number: e?.property_contact_id?.phone_number,
          email: e?.property_contact_id?.email,
          status: e?.property_contact_id?.status,
        };
      });
    },
  });

  const checkAtLeastAPropertyIsActivated = () => {
    let isActivated = false;
    if (apiAllPropertyContactData) {
      apiAllPropertyContactData.forEach((property) => {
        if (property.status === ONBOARDING_PROPERTY_STATUS.ACTIVE) {
          isActivated = true;
        }
      });
    }
    return isActivated;
  };

  const {
    filteredTableData,
    searchedColumns,
    searchedWords,
    updateSearchMeta,
    searchMeta,
  } = useTableSearch({
    searchableColumns,
    allData: apiAllPropertyContactData ?? [],
  });

  useEffect(() => {
    updateSearchMeta(customer, "searchCustomer", ["customer_id"]);
  }, [isFetchedAllPropertContacts, customer]);

  const propertiesData = useMemo(() => {
    let propertyArray;
    if (Object.keys(searchMeta).length > 0 && filteredTableData) {
      propertyArray = [...filteredTableData];
    } else {
      propertyArray = apiAllPropertyContactData;
    }
    return propertyArray;
  }, [apiAllPropertyContactData, searchMeta, filteredTableData]);

  return (
    <>
      <PropertyContactUsersTopBar
        updateSearchMeta={updateSearchMeta}
        setCustomer={setCustomer}
        customer={customer}
        addPropertyUser={addPropertyUser}
        disableAddPropertyUser={checkAtLeastAPropertyIsActivated()}
      />
      {!Array.isArray(propertiesData) || propertiesData.length === 0 ? (
        <Empty description="No Property Contact Found" />
      ) : (
        <div>
          {propertiesData && propertiesData.length > 0 ? (
            <HomePageTable>
              <Table
                className="onboarding-properties-table"
                columns={columns}
                dataSource={propertiesData}
                rowKey="_id"
                scroll={{ x: 1100, y: tableHeight }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                }}
                locale={{
                  emptyText: isFetchedAllPropertContacts ? (
                    <div className="spinner-table">
                      <LoadingComponent />
                    </div>
                  ) : null,
                }}
              />
            </HomePageTable>
          ) : (
            <Empty description="No Property contact found" />
          )}
        </div>
      )}
    </>
  );
};

export default PropertyContactUsers;
